import React, { useState } from 'react';
import { Moon, Menu, X, Heart, Phone, Mail, MessageCircle, Hospital, Baby, Shield, Star, Award, Clock } from 'lucide-react';
import childrenImage from './image.jpg';
import infantImage1 from './infant-image1.jpg';
import infantImage2 from './infant-image2.jpg';
import NavBar from './NavBar';

const AboutPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const services = [
    {
      title: "Garde de Nuit",
      description: "Service professionnel de garde nocturne adapté à vos besoins",
      icon: Moon
    },
    {
      title: "Soins Spécialisés",
      description: "Bain enveloppant, consultation lactation, réflexologie",
      icon: Heart
    },
    {
      title: "Accompagnement Post-Natal",
      description: "Soutien personnalisé après la maternité à votre domicile",
      icon: Baby
    }
  ];

  const expertise = [
    {
      title: "Équipe Qualifiée",
      description: "Auxiliaires de puériculture, infirmières, aides-soignantes spécialisées en petite enfance",
      icon: Shield
    },
    {
      title: "Expérience Variée",
      description: "Notre équipe cumule des années d'expérience en crèche et en milieu hospitalier",
      icon: Award
    },
    {
      title: "Disponibilité Flexible",
      description: "Services adaptables selon vos besoins, de jour comme de nuit",
      icon: Clock
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <NavBar />

      {/* Hero Section avec fond bleu ciel et nuages animés */}
      <div className="relative bg-sky-100 overflow-hidden">
        <div className="absolute inset-0">
          <div className="cloud cloud-1" />
          <div className="cloud cloud-2" />
          <div className="cloud cloud-3" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 py-12 md:py-20">
          <h1 className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 md:mb-6">
            Qui Sommes-Nous ?
          </h1>
          <p className="text-lg md:text-xl text-slate-600">
            Une équipe de professionnelles de santé dédiée au bien-être de vos bébés
          </p>
        </div>
      </div>

      {/* Notre Engagement avec images supplémentaires */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            Notre Engagement
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-2/3">
              <p className="text-slate-600 mb-4">
                Notre équipe de professionnelles de santé est passionnée par le bien-être des bébés. 
                Avec une expérience solide en crèche et en milieu hospitalier, nous comprenons 
                l'importance d'un accompagnement personnalisé pour chaque famille, dès la sortie de la maternité.
              </p>
              <p className="text-slate-600 mb-4">
                Nous nous engageons à fournir des soins de qualité, adaptés aux besoins spécifiques 
                de votre bébé, dans le confort de votre domicile.
              </p>
              <div className="text-slate-600 mb-3">
                <p className="mb-3 font-medium text-amber-500">Notre philosophie :</p>
                <p className="mb-3">
                  Chaque bébé est unique. Notre approche personnalisée tient compte des besoins spécifiques de votre enfant et de votre famille.
                </p>
                <p className="mb-3">
                  Nous créons un environnement sécurisant et apaisant, propice au développement et à l'épanouissement du nouveau-né.
                </p>
                <p className="text-sm italic text-slate-500">
                  Une présence rassurante pour votre famille
                </p>
              </div>
            </div>
            <div className="md:w-1/3 p-4 bg-sky-50 rounded-lg border border-amber-100 shadow-md relative">
              <span className="absolute -top-2 -left-2 text-amber-400 text-lg">✦</span>
              <span className="absolute -top-3 right-8 text-amber-300 text-xl">✦</span>
              <div className="text-center mb-3 text-amber-500 font-medium">
                Notre vision
              </div>
              <img 
                src={childrenImage} 
                alt="Professionnelle avec un bébé" 
                className="rounded-lg w-full h-auto object-cover mx-auto mb-3"
              />
              <p className="text-xs text-center text-slate-500 italic mt-2 relative">
                <span className="absolute -top-3 left-4 text-amber-200 text-xs">✦</span>
                <span className="absolute -top-5 right-12 text-amber-300 text-sm">✦</span>
                Notre équipe accompagne votre bébé dans un cadre chaleureux et bienveillant
              </p>
              <span className="absolute -bottom-2 -right-2 text-amber-400 text-lg">✦</span>
            </div>
          </div>
        </div>

        {/* Section Galerie d'images */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Un Service à Visage Humain</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-sky-50 rounded-lg p-4 shadow-md">
              <img 
                src={infantImage1} 
                alt="Soin d'un nouveau-né" 
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-medium text-slate-800 mb-2">Soins Personnalisés</h3>
              <p className="text-slate-600">Chaque bébé reçoit une attention particulière adaptée à ses besoins et à son rythme propre.</p>
            </div>
            <div className="bg-sky-50 rounded-lg p-4 shadow-md">
              <img 
                src={infantImage2} 
                alt="Moment de tendresse" 
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <h3 className="text-lg font-medium text-slate-800 mb-2">Accompagnement Bienveillant</h3>
              <p className="text-slate-600">Nous créons un lien de confiance avec votre bébé pour assurer son confort et sa sécurité affective.</p>
            </div>
          </div>
        </div>

        {/* Section Contact */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Contactez-Nous</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="tel:0609321931" 
              className="flex items-center space-x-3 text-slate-600 hover:text-amber-500">
              <Phone className="w-6 h-6" />
              <span>06 09 32 19 31</span>
            </a>
            <a href="https://wa.me/33609321931" 
              className="flex items-center space-x-3 text-slate-600 hover:text-amber-500">
              <MessageCircle className="w-6 h-6" />
              <span>WhatsApp</span>
            </a>
            <a href="mailto:etoileveilleuse@gmail.com"  
              className="flex items-center space-x-3 text-slate-600 hover:text-amber-500">
              <Mail className="w-6 h-6" />
              <span>Email</span>
            </a>
          </div>
        </div>

        {/* Notre Expertise */}
        <h2 className="text-2xl font-bold text-slate-800 mb-6">Notre Expertise</h2>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {expertise.map((item, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 transform transition hover:shadow-lg hover:scale-105">
              <item.icon className="w-12 h-12 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 mb-3">{item.title}</h3>
              <p className="text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Nos Services */}
        <h2 className="text-2xl font-bold text-slate-800 mb-6">Nos Services</h2>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 transform transition hover:shadow-lg hover:scale-105">
              <service.icon className="w-12 h-12 text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 mb-3">{service.title}</h3>
              <p className="text-slate-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Styles pour les nuages et les étoiles animées */}
      <style jsx>{`
        .cloud {
          position: absolute;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 100px;
          animation: float 20s infinite linear;
        }

        .cloud-1 {
          width: 200px;
          height: 60px;
          top: 20%;
          left: -200px;
          animation-duration: 25s;
        }

        .cloud-2 {
          width: 150px;
          height: 45px;
          top: 40%;
          left: -150px;
          animation-duration: 30s;
          animation-delay: -10s;
        }

        .cloud-3 {
          width: 180px;
          height: 54px;
          top: 60%;
          left: -180px;
          animation-duration: 35s;
          animation-delay: -5s;
        }

        @keyframes float {
          from { transform: translateX(-100%); }
          to { transform: translateX(100vw); }
        }

        @keyframes twinkle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @media (max-width: 768px) {
          .cloud {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;